import cx from "classnames";
import { useState } from "react";
import { Column, ColumnResizer } from "react-aria-components";

import { ChevronDownSmall, ChevronUpDown, ChevronUpSmall, Dots } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";

import styles from "./styles.module.css";
import { ColumnConfigValue, ColumnSize } from "./types";

type TableColumnProps = {
  value: ColumnConfigValue;
  size?: ColumnSize;
  shouldGrow?: boolean;
  isRowHeader?: boolean;
  hideColumn?: (id: string) => void;
};

const getSortIcon = (sortDirection: "ascending" | "descending" | undefined) => {
  if (sortDirection === "descending") {
    return ChevronDownSmall;
  }

  if (sortDirection === "ascending") {
    return ChevronUpSmall;
  }

  return ChevronUpDown;
};

const TableColumn = ({ value, isRowHeader, hideColumn, size }: TableColumnProps) => {
  const {
    id,
    title,
    sticky,
    resizable,
    icon,
    defaultWidth,
    width,
    noHeader,
    textValue,
    iconOnly,
    ...columnProps
  } = value;

  const [dropdownActive, setDropdownActive] = useState(false);

  const canHide = !columnProps.static && !isRowHeader;

  const a11yValue = title || textValue;

  return (
    <Column
      {...columnProps}
      key={id}
      width={width}
      defaultWidth={size || defaultWidth}
      className={cx(
        styles.column,
        sticky && styles.columnSticky,
        dropdownActive && styles.active,
        columnProps.allowsSorting && styles.sortable,
        canHide && styles.interactive
      )}
      textValue={a11yValue}
      isRowHeader={isRowHeader}
      id={id}
    >
      {({ sortDirection, allowsSorting }) => {
        if (noHeader) {
          return <span className="sr-only">{a11yValue}</span>;
        }

        return (
          <Box gap="small" align="center" justify="between">
            <Box
              align="center"
              gap="small"
              tabIndex={allowsSorting ? 0 : undefined}
              className={styles.columnHeader}
            >
              {icon && <Icon className={styles.columnNameIcon} src={icon} color="secondary" />}
              {iconOnly && <span className="sr-only">{a11yValue}</span>}

              {title && !iconOnly && (
                <Typography
                  className={styles.columnName}
                  color="secondary"
                  tag="span"
                  variant="p-t7"
                >
                  {title}
                </Typography>
              )}
              {allowsSorting && (
                <Icon
                  color="secondary"
                  className={styles.columnCellSortableIcon}
                  src={getSortIcon(sortDirection)}
                />
              )}
            </Box>
            {(canHide || resizable) && (
              <Box align="center">
                {canHide && (
                  <DropdownMenu
                    onOpenChange={setDropdownActive}
                    triggerComponent={
                      <ButtonIcon
                        className={styles.columnDropdownEllipsis}
                        icon={Dots}
                        variant="ghost"
                      >
                        Column options
                      </ButtonIcon>
                    }
                  >
                    <DropdownMenuItem onAction={() => (hideColumn?.(id as string), close)}>
                      Hide
                    </DropdownMenuItem>
                  </DropdownMenu>
                )}
                {resizable && <ColumnResizer className={styles.columnResizer} />}
              </Box>
            )}
          </Box>
        );
      }}
    </Column>
  );
};

export default TableColumn;
